/*
 * Carbisdale - DictationManager
 *
 * Zentrale Steuerung fuer das Diktat-Feature.
 * Koordiniert Audio, WebSocket, Post-Processing und Editor-Integration.
 *
 * Zustandsmaschine:
 * IDLE → STARTING → ACTIVE → STOPPING → IDLE
 *
 * Aktivierung: Doppel-Ctrl (Start) / Einfacher Ctrl (Stopp)
 */

import { DictationAudio } from './DictationAudio.js'
import { DictationSocket } from './DictationSocket.js'
import { processDictationText, createPostProcessorState } from './DictationPostProcessor.js'
import { DICTATION } from '../config.js'

export const DictationState = Object.freeze({
    IDLE: 'IDLE',
    STARTING: 'STARTING',     // Token holen, Mikro starten, WebSocket verbinden
    ACTIVE: 'ACTIVE',         // Diktat laeuft, Audio wird gestreamt
    STOPPING: 'STOPPING'      // Aufraeumen
})

export class DictationManager {
    /**
     * @param {object} options
     * @param {object} options.soundManager - SoundManager-Instanz
     */
    constructor({ soundManager }) {
        this._state = DictationState.IDLE
        this._audio = new DictationAudio()
        this._socket = new DictationSocket()
        this._soundManager = soundManager
        this._postProcessorState = createPostProcessorState()

        // Token-Cache (spart Roundtrip bei erneutem Diktat-Start)
        this._cachedToken = null       // { token, fetchedAt }
        this._tokenMaxAge = 100 * 1000 // Token wiederverwenden wenn juenger als 100s (TTL ist 120s)

        // Doppel-Ctrl-Tracking
        this._lastCtrlUpTime = 0
        this._ctrlPressedAlone = false

        // Zeitstempel des letzten Uebergangs nach ACTIVE — wird in stop()
        // genutzt, um die echte Diktat-Dauer ans Backend zu melden.
        this._activeSinceMs = 0

        // Interim-Tracking
        this._currentInterimText = ''   // Aktueller Interim-Text (fuer Replacement)
        this._hasOpenInterim = false    // Ob ein Interim-Text als Selection steht
        this._needsLeadingSpace = false // Ob vor dem naechsten Text ein Leerzeichen eingefuegt werden muss
        this._lastCommittedEndChar = null // Letztes committetes Zeichen — Dedup ueber Final-Grenzen (". .")

        // === Callbacks (werden von main.js gesetzt) ===

        /** Interim-Text einfuegen (ersetzt vorherigen Interim) */
        this.onInterim = null       // (text: string) => void

        /** Finalen Text committen */
        this.onFinal = null          // (text: string) => void

        /** Absatz-/Zeilenumbruch ausloesen */
        this.onBreak = null          // () => void

        /** State hat sich geaendert */
        this.onStateChange = null    // (state: string) => void

        /** Zeichen vor dem Cursor abfragen (fuer Leerzeichen-Logik) */
        this.onGetCharBeforeCursor = null  // () => string|null

        /** Fehler aufgetreten */
        this.onError = null          // (message: string) => void

        // Socket-Callbacks verdrahten
        this._socket.onTranscript = (text, isFinal) => this._handleTranscript(text, isFinal)
        this._socket.onUtteranceEnd = () => this._handleUtteranceEnd()
        this._socket.onError = (err) => this._handleError(err)
        this._socket.onClose = () => this._handleSocketClose()
    }

    // === Public API ===

    /** @returns {string} Aktueller Zustand */
    get state() { return this._state }

    /** @returns {boolean} Diktat laeuft */
    get isActive() { return this._state === DictationState.ACTIVE }

    /** @returns {boolean} Ob ein nicht-finaler Interim-Text offen ist */
    get hasOpenInterim() { return this._hasOpenInterim }

    /**
     * Startet das Diktat: Token holen → Mikro → WebSocket.
     */
    async start() {
        if (this._state !== DictationState.IDLE) return

        // Audio-Support pruefen
        if (!DictationAudio.isSupported()) {
            this._reportError('Dictation is not supported by this browser')
            return
        }

        this._setState(DictationState.STARTING)
        this._postProcessorState = createPostProcessorState()
        this._currentInterimText = ''
        this._hasOpenInterim = false

        // Pruefen ob vor dem Cursor schon Text steht (fuer Leerzeichen-Logik)
        const charBefore = this.onGetCharBeforeCursor ? this.onGetCharBeforeCursor() : null
        this._needsLeadingSpace = charBefore !== null && charBefore !== '' && !/\s/.test(charBefore)
        // Auch gegen bestehenden Text deduplizieren (Diktat-Neustart nach ".")
        this._lastCommittedEndChar = charBefore || null

        try {
            // 1. Token vom Backend holen
            const tokenData = await this._fetchToken()
            if (this._state !== DictationState.STARTING) return  // Abgebrochen

            // 2. Mikrofon starten
            await this._audio.start()
            if (this._state !== DictationState.STARTING) return

            // 3. WebSocket verbinden
            this._socket.connect(tokenData.token)

            // Audio-Chunks an Socket weiterleiten
            this._audio.onAudioData = (chunk) => {
                this._socket.send(chunk)
            }

            // Warte auf WebSocket-Open
            await new Promise((resolve, reject) => {
                const prevOnOpen = this._socket.onOpen
                const prevOnError = this._socket.onError
                const restore = () => {
                    this._socket.onOpen = prevOnOpen
                    this._socket.onError = prevOnError
                }
                // Timeout nach 5 Sekunden
                const timer = setTimeout(() => { restore(); reject(new Error('WebSocket timeout')) }, 5000)
                this._socket.onOpen = () => {
                    clearTimeout(timer)
                    restore()
                    resolve()
                }
                this._socket.onError = (err) => {
                    clearTimeout(timer)
                    restore()
                    reject(err)
                }
            })

            if (this._state !== DictationState.STARTING) return

            this._setState(DictationState.ACTIVE)
            this._activeSinceMs = Date.now()
            this._soundManager.playDictationStart()

        } catch (err) {
            this._cleanup()
            this._setState(DictationState.IDLE)
            this._reportError(this._friendlyStartError(err))
        }
    }

    /**
     * Uebersetzt technische Start-Fehler in verstaendliche Meldungen —
     * insbesondere die Mikrofon-Berechtigung (getUserMedia-DOMExceptions),
     * bei der Erstnutzer sonst nur ein rohes "Permission denied" saehen.
     * @param {Error} err
     * @returns {string}
     */
    _friendlyStartError(err) {
        if (err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
            return 'Microphone access is blocked — please allow it in your browser settings and try again'
        }
        if (err && (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError')) {
            return 'No microphone found — please connect one and try again'
        }
        return err.message || 'Dictation could not be started'
    }

    /**
     * Stoppt das Diktat.
     */
    stop() {
        if (this._state !== DictationState.ACTIVE && this._state !== DictationState.STARTING) return

        // Dauer-Tracking nur wenn wir in ACTIVE waren. STARTING→STOPPING
        // heisst der Audio-Stream ist nie gelaufen.
        const wasActive = this._state === DictationState.ACTIVE
        const activeSeconds = wasActive && this._activeSinceMs > 0
            ? Math.max(0, (Date.now() - this._activeSinceMs) / 1000)
            : 0
        this._activeSinceMs = 0

        this._setState(DictationState.STOPPING)
        this._soundManager.playDictationStop()

        // Socket sauber schliessen (wartet auf letzte Finals)
        this._socket.close()

        // Audio sofort stoppen
        this._audio.stop()

        // Letzter Interim bleibt als Selection stehen (User entscheidet)
        // _hasOpenInterim bleibt auf true wenn es einen offenen Interim gibt

        this._setState(DictationState.IDLE)

        // Tatsaechliche Dauer ans Backend melden (fire-and-forget).
        // Darf das UI-Erlebnis nicht blockieren.
        if (activeSeconds > 0) {
            this._reportUsage(activeSeconds)
        }
    }

    /**
     * Meldet die echte Diktat-Dauer ans Backend (Usage-Tracking).
     * @param {number} seconds
     */
    _reportUsage(seconds) {
        try {
            fetch(DICTATION.usageEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ seconds })
            }).catch(() => { /* Usage-Report darf nicht stoeren */ })
        } catch { /* never throws */ }
    }

    /**
     * Verarbeitet Ctrl-Tastendruck (keydown).
     * Setzt _ctrlPressedAlone auf true — wird false wenn andere Taste dazukommt.
     */
    handleCtrlDown() {
        this._ctrlPressedAlone = true
    }

    /**
     * Markiert dass Ctrl NICHT allein gedrueckt wurde (z.B. Ctrl+C).
     */
    handleCtrlCombination() {
        this._ctrlPressedAlone = false
    }

    /**
     * Verarbeitet Ctrl-Loslassen (keyup).
     * Implementiert Doppel-Ctrl-Erkennung.
     * @returns {boolean} true wenn eine Aktion ausgefuehrt wurde
     */
    handleCtrlUp() {
        if (!this._ctrlPressedAlone) return false
        this._ctrlPressedAlone = false

        // Diktat aktiv → Stopp
        if (this.isActive || this._state === DictationState.STARTING) {
            this.stop()
            return true
        }

        // Doppel-Ctrl → Start
        const now = Date.now()
        if (now - this._lastCtrlUpTime < DICTATION.doubleCtrlTimeout) {
            this._lastCtrlUpTime = 0
            this.start()
            return true
        }

        this._lastCtrlUpTime = now
        return false
    }

    /**
     * Signalisiert dem Manager, dass der Interim-Text vom User akzeptiert wurde
     * (z.B. durch Tippen, Klick, Pfeiltaste).
     */
    acceptInterim() {
        // Interim wird zu festem Text — letztes Zeichen fuer den
        // Cross-Chunk-Dedup uebernehmen
        const trimmed = this._currentInterimText.trimEnd()
        if (trimmed) this._lastCommittedEndChar = trimmed[trimmed.length - 1]
        this._hasOpenInterim = false
        this._currentInterimText = ''
    }

    // === Interne Event-Handler ===

    /**
     * Verarbeitet ein Transkript von Deepgram.
     * @param {string} rawText - Erkannter Text
     * @param {boolean} isFinal - true wenn Segment finalisiert
     */
    _handleTranscript(rawText, isFinal) {
        // Post-Processing: Diktatbefehle (DE/EN) anwenden
        const result = processDictationText(rawText, this._postProcessorState)
        let text = result.text

        // Interpunktions-Verdopplung bereinigen:
        // Deepgram setzt automatisch Interpunktion UND PostProcessor
        // ersetzt Befehle ("Komma") → kann zu ",,", "..." oder ". ."
        // (mit Leerzeichen dazwischen) fuehren
        text = text.replace(/([.,!?:;])(\s*\1)+/g, '$1')

        // Dedup ueber Final-Grenzen: endete der zuletzt committete Text mit
        // demselben Satzzeichen, mit dem dieser Chunk beginnt, ist das eine
        // Verdopplung ueber zwei Chunks ("…Ende." + ". Und…" → ". .")
        if (text && '.,!?:;'.includes(text[0]) && text[0] === this._lastCommittedEndChar) {
            text = text.slice(1).replace(/^\s+/, '')
        }

        if (!text) {
            if (!isFinal) return
            // Reiner Befehls-Chunk ("Neue Zeile" allein): eventuell offenes
            // Interim entfernen und die Umbrueche trotzdem ausfuehren
            if (this._hasOpenInterim && this.onFinal) this.onFinal('')
            this._currentInterimText = ''
            this._hasOpenInterim = false
            for (const _breakOffset of result.breaks) {
                if (this.onBreak) this.onBreak()
                this._needsLeadingSpace = false
                this._lastCommittedEndChar = null  // Neuer Absatz beginnt leer
            }
            return
        }

        // Fuehrendes Leerzeichen einfuegen wenn noetig:
        // Nach einem Final oder bei Diktat-Neustart nach bestehendem Text.
        // Das Leerzeichen wird Teil des Textes und bei Interim-Ersetzung
        // als Teil der Selection mit-selektiert und mit-ersetzt.
        if (this._needsLeadingSpace) {
            text = ' ' + text
        }


        if (isFinal) {
            // Finaler Text: committen
            if (this.onFinal) this.onFinal(text)
            this._currentInterimText = ''
            this._hasOpenInterim = false
            // Nach Final braucht der naechste Text ein Leerzeichen,
            // es sei denn der Text endet mit Whitespace oder Umbruch
            this._needsLeadingSpace = text.length > 0 && !/\s$/.test(text)
            // Letztes Zeichen fuer den Cross-Chunk-Dedup merken
            const trimmed = text.trimEnd()
            if (trimmed) this._lastCommittedEndChar = trimmed[trimmed.length - 1]

            // Umbrueche nach dem Final ausfuehren
            for (const _breakOffset of result.breaks) {
                if (this.onBreak) this.onBreak()
                this._needsLeadingSpace = false  // Nach Umbruch kein Leerzeichen
                this._lastCommittedEndChar = null  // Neuer Absatz beginnt leer
            }
        } else {
            // Interim-Text: als Selection anzeigen
            this._currentInterimText = text
            this._hasOpenInterim = true
            if (this.onInterim) this.onInterim(text)
        }
    }

    /**
     * Deepgram meldet laengere Stille (UtteranceEnd).
     */
    _handleUtteranceEnd() {
        // Guard: Nur im aktiven Zustand reagieren (nach stop() koennten
        // noch Events eintreffen, da der WebSocket 500ms offen bleibt)
        if (this._state !== DictationState.ACTIVE) return
        if (this.onBreak) this.onBreak()
    }

    /**
     * WebSocket-Fehler behandeln.
     * @param {Error} err
     */
    _handleError(err) {
        console.error('Diktat-Fehler:', err.message)
        if (this._state === DictationState.ACTIVE) {
            this.stop()
            this._reportError('Voice connection lost')
        }
    }

    /**
     * WebSocket wurde geschlossen.
     */
    _handleSocketClose() {
        if (this._state === DictationState.ACTIVE) {
            // Unerwartetes Schliessen
            this._audio.stop()
            this._setState(DictationState.IDLE)
        }
    }

    // === Interne Hilfsfunktionen ===

    /**
     * Holt ein temporaeres Deepgram-Token vom Backend.
     * Verwendet gecachtes Token wenn es noch ausreichend frisch ist
     * (juenger als _tokenMaxAge), um den Diktat-Start zu beschleunigen.
     * @returns {Promise<{token: string, expiresIn: number}>}
     */
    async _fetchToken() {
        // Cache pruefen: Token wiederverwenden wenn noch frisch genug
        if (this._cachedToken) {
            const age = Date.now() - this._cachedToken.fetchedAt
            if (age < this._tokenMaxAge) {
                return this._cachedToken
            }
            this._cachedToken = null
        }

        const response = await fetch(DICTATION.tokenEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })

        if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            throw new Error(data.error || `Token-Anfrage fehlgeschlagen (${response.status})`)
        }

        const data = await response.json()
        this._cachedToken = { ...data, fetchedAt: Date.now() }
        return data
    }

    /**
     * Gibt alle Ressourcen frei.
     */
    _cleanup() {
        this._audio.stop()
        this._socket.destroy()
        this._currentInterimText = ''
        this._hasOpenInterim = false
        this._needsLeadingSpace = false
    }

    /**
     * Setzt den Zustand und benachrichtigt Listener.
     * @param {string} newState
     */
    _setState(newState) {
        if (this._state === newState) return
        this._state = newState
        if (this.onStateChange) this.onStateChange(newState)
    }

    /**
     * Meldet einen Fehler.
     * @param {string} message
     */
    _reportError(message) {
        this._soundManager.playPing()
        if (this.onError) this.onError(message)
    }
}
